import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client.js';
import { useToast } from '../state/ToastContext.jsx';
import { useMeta } from '../state/MetaContext.jsx';
import { formatDate, formatDateTime, formatXaf, titleCase } from '../lib/format.js';
import { Field, Modal, StatusBadge } from './ui.jsx';
import SignaturePad from './SignaturePad.jsx';

// Pieces shared by the landlord's lease page, the tenant's copy of it, and the
// printable version. Keeping them together means the tenant never sees a slightly
// different agreement from the one the landlord signed.

const STATUS_LABELS = {
  draft: 'Draft',
  awaiting_signatures: 'Waiting for signatures',
  active: 'In force',
  ended: 'Ended',
  terminated: 'Terminated',
  cancelled: 'Cancelled'
};

const STATUS_TONES = {
  draft: 'muted',
  awaiting_signatures: 'warn',
  active: 'good',
  ended: 'info',
  terminated: 'bad',
  cancelled: 'muted'
};

export const LeaseStatus = ({ status }) => (
  <span className={`badge badge-${STATUS_TONES[status] || 'muted'}`}>{STATUS_LABELS[status] || titleCase(status)}</span>
);

const SignatureBlock = ({ title, party, signature, awaiting }) => (
  <div className="signature-block">
    <span className="signature-role">{title}</span>
    {signature ? (
      <>
        <img className="signature-image" src={signature.url} alt={`Signature of ${signature.name}`} />
        <span className="signature-name">{signature.name}</span>
        <span className="signature-date">Signed {formatDateTime(signature.signedAt)}</span>
      </>
    ) : (
      <>
        <span className="signature-empty">{awaiting ? 'Not signed yet' : ''}</span>
        <span className="signature-name">{party || ''}</span>
      </>
    )}
  </div>
);

/**
 * The agreement itself. `forPrint` drops the interface chrome so a browser's print
 * to PDF produces something a landlord can hand over or keep on file.
 */
export const LeaseDocument = ({ tenancy, platformName, forPrint = false }) => {
  const place = [tenancy.unit?.label, tenancy.property?.title].filter(Boolean).join(', ');
  const address = [tenancy.property?.address, tenancy.property?.neighbourhood, tenancy.property?.city]
    .filter(Boolean)
    .join(', ');

  // The agreement is issued by the landlord, not by us, so it carries their name at
  // the top the way a letterhead would. A landlord trading under a business name gets
  // that; one letting a room in their own house gets their own name.
  const issuedBy = tenancy.landlord?.businessName || tenancy.landlord?.name;

  return (
    <article className={`lease-document ${forPrint ? 'is-print' : ''}`}>
      <header className="lease-doc-head">
        <div>
          {tenancy.landlord?.logoUrl ? <img className="doc-logo" src={tenancy.landlord.logoUrl} alt="" /> : null}
          {issuedBy ? <p className="lease-letterhead">{issuedBy}</p> : null}
          <h2>Tenancy agreement</h2>
          <p className="muted">
            Reference NDK-L{String(tenancy.id).padStart(5, '0')}
            {platformName ? ` - prepared through ${platformName}` : ''}
          </p>
        </div>
        {!forPrint ? <LeaseStatus status={tenancy.status} /> : null}
      </header>

      <dl className="lease-parties">
        <div>
          <dt>Landlord</dt>
          <dd>
            {tenancy.landlord?.name}
            {tenancy.landlord?.phone ? <span className="muted"> {tenancy.landlord.phone}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Tenant</dt>
          <dd>
            {tenancy.tenant?.name}
            {tenancy.tenant?.phone ? <span className="muted"> {tenancy.tenant.phone}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Premises</dt>
          <dd>
            {place}
            {address ? <span className="muted"> - {address}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Term</dt>
          <dd>
            From {formatDate(tenancy.startDate)}
            {tenancy.endDate ? ` to ${formatDate(tenancy.endDate)}` : ', month to month'}
          </dd>
        </div>
        <div>
          <dt>Rent</dt>
          <dd>
            {formatXaf(tenancy.rent)}, due on day {tenancy.rentDueDay} of each period
          </dd>
        </div>
        <div>
          <dt>Deposit</dt>
          <dd>{tenancy.deposit > 0 ? formatXaf(tenancy.deposit) : 'None'}</dd>
        </div>
        {/* Only worth a line on the agreement when the landlord actually supplies it. */}
        {tenancy.internet > 0 ? (
          <div>
            <dt>Internet</dt>
            <dd>{formatXaf(tenancy.internet)} per period, billed separately from rent and due on the 27th of each month</dd>
          </div>
        ) : null}
      </dl>

      <div className="lease-terms">{tenancy.terms}</div>

      {tenancy.specialConditions ? (
        <section className="lease-special">
          <h3>11. CONDITIONS SPECIFIC TO THIS TENANCY</h3>
          <p>{tenancy.specialConditions}</p>
        </section>
      ) : null}

      <footer className="lease-signatures">
        <SignatureBlock
          title="The landlord"
          party={tenancy.landlord?.name}
          signature={tenancy.signatures?.landlord}
          awaiting={tenancy.status === 'awaiting_signatures'}
        />
        <SignatureBlock
          title="The tenant"
          party={tenancy.tenant?.name}
          signature={tenancy.signatures?.tenant}
          awaiting={tenancy.status === 'awaiting_signatures'}
        />
      </footer>

      {tenancy.endedOn ? (
        <p className="lease-ended">
          This tenancy ended on {formatDate(tenancy.endedOn)}. {tenancy.endReason}
        </p>
      ) : null}
    </article>
  );
};

/** Draw-and-sign dialog, used by whichever party is signing. */
export const SignLeaseModal = ({ tenancy, defaultName, onClose, onSigned }) => {
  const toast = useToast();
  const [name, setName] = useState(defaultName || '');
  const [signature, setSignature] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!signature) {
      toast.error('Draw your signature in the box first.');
      return;
    }

    setBusy(true);
    try {
      const form = new FormData();
      form.append('name', name.trim());
      form.append('signature', signature, 'signature.png');

      const result = await api.upload(`/tenancies/${tenancy.id}/sign`, form);
      toast.success(
        result.activated
          ? 'Both parties have signed. The lease is now in force and the rent schedule has been created.'
          : 'Signed. Waiting for the other party.'
      );
      onSigned(result);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Sign the tenancy agreement"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="sign-form"
            className="btn btn-primary"
            disabled={busy || !agreed || !signature || name.trim().length < 2}
          >
            {busy ? 'Signing...' : 'Sign the agreement'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        Signing binds you to the agreement you have just read. The date, your device and your network address are
        recorded alongside your signature.
      </p>

      <form id="sign-form" className="stack" onSubmit={submit}>
        <Field label="Your full name, as it should appear on the agreement" required>
          <input type="text" value={name} onChange={(event) => setName(event.target.value)} required />
        </Field>

        <Field label="Your signature" required>
          <SignaturePad onChange={setSignature} label="Draw your signature here" />
        </Field>

        <label className="check">
          <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
          <span>
            I have read the whole agreement and I accept it. I understand this signature has the same effect as signing
            on paper.
          </span>
        </label>
      </form>
    </Modal>
  );
};

/** Charges: what has been billed, what is still owing, what is overdue. */
/** The usual reasons a landlord lets a late fee go, offered as one-tap answers. */
export const LATE_FEE_REASONS = [
  'Paid on time, recorded late',
  'Agreed extra time to pay',
  'First time late',
  'Hardship this month',
  'Our mistake'
];

export const ChargeTable = ({ charges, onPay, onWaive, payLabel = 'Record payment' }) => {
  const { chargeKinds } = useMeta();
  const kindLabel = (slug) => chargeKinds.find((kind) => kind.slug === slug)?.label || titleCase(slug);
  const visibleCharges = charges.filter(
    (charge) => !(charge.status === 'waived' && charge.waivedReason === 'Platform accounting reset for clean restart')
  );

  if (visibleCharges.length === 0) {
    return <p className="muted">Nothing has been billed yet.</p>;
  }

  return (
    <div className="table-wrap">
      <table className="data-table ledger-table">
        <thead>
          <tr>
            <th>What</th>
            <th>Due</th>
            <th className="numeric">Amount</th>
            <th className="numeric">Paid</th>
            <th className="numeric">Outstanding</th>
            <th>Status</th>
            {onPay || onWaive ? <th>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {visibleCharges.map((charge) => (
            <tr key={charge.id} className={charge.overdue ? 'is-overdue' : ''}>
              <td data-label="What">
                <strong>{charge.label}</strong>
                {!['rent', 'deposit'].includes(charge.kind) ? (
                  <span className="muted"> - {kindLabel(charge.kind)}</span>
                ) : null}
                {charge.waivedReason ? (
                  <span className="muted">
                    {' '}
                    {charge.kind === 'penalty' ? 'Waived' : 'Written off'}: {charge.waivedReason}
                  </span>
                ) : null}
              </td>
              <td data-label="Due">
                {formatDate(charge.dueDate)}
                {charge.overdue ? <span className="overdue-flag"> overdue</span> : null}
              </td>
              <td data-label="Amount" className="numeric">
                {formatXaf(charge.amount)}
              </td>
              <td data-label="Paid" className="numeric">
                {formatXaf(charge.paid ?? 0)}
              </td>
              <td data-label="Outstanding" className="numeric">
                {formatXaf(charge.outstanding ?? 0)}
              </td>
              <td data-label="Status">
                <StatusBadge
                  status={
                    charge.status === 'paid'
                      ? 'approved'
                      : charge.status === 'partly_paid'
                        ? 'pending'
                        : charge.status === 'waived'
                          ? 'withdrawn'
                          : charge.overdue
                            ? 'rejected'
                            : 'submitted'
                  }
                  label={
                    charge.status === 'partly_paid'
                      ? 'Part paid'
                      : charge.status === 'waived'
                        ? charge.kind === 'penalty'
                          ? 'Waived'
                          : 'Written off'
                        : charge.status === 'due' && charge.overdue
                          ? 'Overdue'
                          : titleCase(charge.status)
                  }
                />
              </td>
              {onPay || onWaive ? (
                <td data-label="Actions">
                  <div className="cell-actions">
                    {onPay && charge.outstanding > 0 ? (
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => onPay(charge)}>
                        {payLabel}
                      </button>
                    ) : null}
                    {onWaive && charge.outstanding > 0 ? (
                      charge.kind === 'penalty' ? (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onWaive(charge)}>
                          Waive late fee
                        </button>
                      ) : (
                        <button type="button" className="link-btn" onClick={() => onWaive(charge)}>
                          Write off
                        </button>
                      )
                    ) : null}
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Receipts issued against a lease. */
export const PaymentTable = ({ payments, onVoid }) => {
  const { paymentMethods } = useMeta();
  const methodLabel = (slug) => paymentMethods.find((method) => method.slug === slug)?.label || titleCase(slug);

  if (payments.length === 0) {
    return <p className="muted">No payments recorded yet.</p>;
  }

  return (
    <div className="table-wrap">
      <table className="data-table ledger-table">
        <thead>
          <tr>
            <th>Receipt</th>
            <th>Paid</th>
            <th>How</th>
            <th className="numeric">Amount</th>
            <th>Recorded by</th>
            {onVoid ? <th>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <tr key={payment.id} className={payment.voided ? 'is-voided' : ''}>
              <td data-label="Receipt">
                <Link to={`/receipts/${payment.id}`}>{payment.receiptNumber}</Link>
                {payment.voided ? <span className="void-flag"> cancelled</span> : null}
              </td>
              <td data-label="Paid">{formatDate(payment.paidAt)}</td>
              <td data-label="How">
                {methodLabel(payment.method)}
                {payment.reference ? <span className="muted"> - {payment.reference}</span> : null}
              </td>
              <td data-label="Amount" className="numeric">
                {formatXaf(payment.amount)}
              </td>
              <td data-label="Recorded by">
                {payment.providerTxnId ? (
                  <span className="muted">Paid online</span>
                ) : (
                  payment.recordedByName || '-'
                )}
              </td>
              {onVoid ? (
                <td data-label="Actions">
                  {payment.voided ? (
                    <span className="muted">{payment.voidReason}</span>
                  ) : (
                    <button type="button" className="link-btn danger-link" onClick={() => onVoid(payment)}>
                      Cancel receipt
                    </button>
                  )}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** The four numbers that matter, above the tables. */
export const LedgerSummary = ({ totals }) => (
  <div className="stat-row">
    <div className="stat-tile">
      <span className="stat-value">{formatXaf(totals.billed)}</span>
      <span className="stat-label">Billed in total</span>
    </div>
    <div className="stat-tile">
      <span className="stat-value">{formatXaf(totals.received)}</span>
      <span className="stat-label">Received</span>
    </div>
    <div className={`stat-tile ${totals.balance > 0 ? 'tone-warn' : ''}`}>
      <span className="stat-value">{formatXaf(Math.abs(totals.balance))}</span>
      <span className="stat-label">{totals.balance > 0 ? 'Outstanding' : totals.balance < 0 ? 'Paid ahead' : 'Nothing owing'}</span>
    </div>
    <div className={`stat-tile ${totals.overdue > 0 ? 'tone-warn' : ''}`}>
      <span className="stat-value">{formatXaf(totals.overdue)}</span>
      <span className="stat-label">Overdue</span>
    </div>
  </div>
);
